import React from "react";
import { WavRecorder } from "src/lib/wavtools/wav_recorder";

// Returns [lowScale, highScale] — multiplicative SVG transforms driven by the
// low/high halves of the live mic frequency spectrum. When the recorder isn't
// active, frequencyData collapses to a single-element Float32Array and the
// split produces NaNs, which the caller treats as "idle".
export const getAmplitude = (wavRecorder: WavRecorder): [number, number] => {
  const result = wavRecorder.recording
    ? wavRecorder.getFrequencies("voice")
    : { values: new Float32Array([0]) };

  const frequencyData = result.values;
  const length = frequencyData.length;
  const midPoint = Math.floor(length / 2);

  const lowFreqs = frequencyData.slice(0, midPoint);
  const lowAmplitude = Math.sqrt(
    lowFreqs.reduce((sum, value) => sum + value ** 2, 0) / midPoint,
  );

  const highFreqs = frequencyData.slice(midPoint);
  const highAmplitude = Math.sqrt(
    highFreqs.reduce((sum, value) => sum + value ** 2, 0) /
      (length - midPoint),
  );

  const scaleLow = 1 + lowAmplitude * 0.5;
  const scaleHigh = 1 + highAmplitude * 0.5;

  return [scaleLow, scaleHigh];
};

// Starts an rAF loop that rotates and scales an SVG path using live mic
// amplitude. Returns a cancel function — callers MUST invoke it to stop the
// loop (previously the loop was unstoppable, leaving rAF callbacks pinned for
// the lifetime of the page).
export const animateAmplitudeBlob = (
  ref: React.RefObject<SVGPathElement>,
  getScale: () => number,
): (() => void) => {
  let angle = 0;
  let direction = Math.random() > 0.5 ? 1 : -1;
  let speed = Math.random() * 0.5 + 0.5;
  let targetSpeed = speed;
  const minSpeed = 0.01;
  const maxSpeed = 1;
  const directionChangeInterval = 2000 + Math.random() * 3000;
  let speedChangeInterval = 500 + Math.random() * 200;
  let lastDirectionChangeTime = Date.now();
  let lastChangeTime = Date.now();
  const pauseProbability = 0.1;
  const pauseDuration = 1000 + Math.random() * 2000;
  let isPaused = false;
  let pauseEndTime = 0;
  let cancelled = false;
  let rafId = 0;

  const rotate = () => {
    if (cancelled) return;
    const now = Date.now();

    if (isPaused) {
      if (now >= pauseEndTime) {
        isPaused = false;
      } else {
        targetSpeed = 0;
      }
    }

    speed += (targetSpeed - speed) * 0.05;
    angle += speed * direction;

    if (ref.current) {
      const scale = getScale();
      ref.current.style.transform = `rotate(${angle}deg) scale(${scale})`;
      ref.current.style.transformOrigin = "center center";
    }

    if (now - lastDirectionChangeTime > directionChangeInterval) {
      lastDirectionChangeTime = now;
      direction = Math.random() > 0.5 ? 1 : -1;
    }
    if (now - lastChangeTime > speedChangeInterval && !isPaused) {
      lastChangeTime = now;
      targetSpeed = Math.random() * (maxSpeed - minSpeed) + minSpeed;
      speedChangeInterval = 500 + Math.random() * 200;

      if (Math.random() < pauseProbability) {
        isPaused = true;
        pauseEndTime = now + pauseDuration;
      }
    }

    rafId = window.requestAnimationFrame(rotate);
  };

  rafId = window.requestAnimationFrame(rotate);

  return () => {
    cancelled = true;
    if (rafId) window.cancelAnimationFrame(rafId);
  };
};
